import { useRef, useState } from "react";

import { PROJECT_MESSAGES } from "../../messages";
import type { FlatStep } from "../../projectTypes";
import BlockList from "../blocks/BlockList";

interface ProjectInstructionsProps {
    step: FlatStep;
    isCompleted: boolean;
    failedStep?: FlatStep; // Step whose "incorrect" feedback is shown after a failed run
    hasNext: boolean;
    onComplete: () => void; // A question in the instructions was answered correctly
    onNext: () => void;
    onHintsOpened?: () => void; // Called the first time the hints of this step are opened
}

// The current step: instructions, hints, feedback and explanation
const ProjectInstructions = ({
                                 step,
                                 isCompleted,
                                 failedStep,
                                 hasNext,
                                 onComplete,
                                 onNext,
                                 onHintsOpened
                             }: ProjectInstructionsProps) => {
    const [showHints, setShowHints] = useState(false);
    const hintsCounted = useRef(false);

    // The page renders this with key={step.id}, so hints close again on every new step
    const hints = step.hints ?? [];

    const toggleHints = () => {
        if (!showHints && !hintsCounted.current) {
            hintsCounted.current = true;
            onHintsOpened?.();
        }
        setShowHints((visible) => !visible);
    };

    return (
        <div className="mb-8">
            <div className="text-xs text-gray-400 mb-1">
                {step.partName} · {PROJECT_MESSAGES.step} {step.stepIndex + 1}
            </div>

            <h2 className="text-lg font-bold mb-4">{PROJECT_MESSAGES.instructions}</h2>

            <BlockList blocks={step.instruction?.blocks} onComplete={onComplete} />

            {hints.length > 0 && (
                <div className="mt-4">
                    <button
                        type="button"
                        aria-expanded={showHints}
                        onClick={toggleHints}
                        className="text-sm text-blue-600 hover:underline"
                    >
                        {showHints ? PROJECT_MESSAGES.hintsHide : PROJECT_MESSAGES.hintsShow}
                    </button>

                    {showHints && (
                        <div className="mt-2 rounded-xl bg-blue-50 p-4">
                            {hints.map((hint, index) => (
                                <BlockList key={hint.id ?? index} blocks={hint.content?.blocks} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {failedStep && (
                <div role="alert" className="mt-6 rounded-xl bg-red-50 border border-red-200 p-4">
                    <BlockList blocks={failedStep.feedback?.incorrect?.blocks} />
                </div>
            )}

            {isCompleted && (
                <div className="mt-6 flex flex-col gap-4">
                    <div className="rounded-xl bg-green-50 border border-green-200 p-4">
                        <BlockList blocks={step.feedback?.correct?.blocks} />
                    </div>

                    {(step.explain?.blocks?.length ?? 0) > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold mb-2">
                                {PROJECT_MESSAGES.explanation}
                            </h3>
                            <BlockList blocks={step.explain?.blocks} />
                        </div>
                    )}

                    {hasNext && (
                        <button
                            type="button"
                            onClick={onNext}
                            className="self-start rounded-xl bg-black text-white px-5 py-2 text-sm hover:bg-gray-800 transition"
                        >
                            {PROJECT_MESSAGES.nextStep}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProjectInstructions;